import React from 'react';
import { Button, Card, Input, Typography, message } from 'antd';
import { MailOutlined } from '@ant-design/icons';
import { usePaymentProfile } from '../context/payment-profile';
import { useSocialContext } from '../context/social';
import { tokens } from '../theme/tokens';

const { Title, Text } = Typography;
const API_BASE = process.env.REACT_APP_API_BASE_URL || '';

// Mismo JWT que el resto de la app (context/social.tsx).
const JWT_KEY = 'drupi_social_jwt';
const authHeaders = (): Record<string, string> => {
  const jwt = localStorage.getItem(JWT_KEY);
  return jwt ? { Authorization: `Bearer ${jwt}` } : {};
};

const EMAIL_RE = /^[^@\s]+@[^@\s]+$/;
const RESEND_COOLDOWN_S = 60;   // espejo del backend (verification_email.RESEND_COOLDOWN_SECONDS)

/** Extrae el `detail` de un error de FastAPI para mostrar el motivo real. */
const detailOf = async (r: Response, fallback: string): Promise<string> => {
  try {
    const d = await r.json();
    if (typeof d?.detail === 'string') return d.detail;
  } catch { /* body no-JSON */ }
  return fallback;
};

/**
 * Pantalla ÚNICA y bloqueante "Verificá tu email" (identidad por email, 2026-07-31).
 *
 * La renderiza email-verification-gate en lugar de la app entera cuando el perfil
 * está completo pero el email todavía no se verificó. El código de 6 dígitos ya se
 * envió al cargar/cambiar el email (PUT /user-profile lo dispara); acá el usuario lo
 * confirma, puede pedir el reenvío (cooldown 60s) o corregir el email si se equivocó.
 * Al confirmar, el backend devuelve un JWT nuevo (con el email como claim) que
 * reemplaza al guardado, y un reload deja pasar a la app.
 */
export const EmailVerificationScreen: React.FC = () => {
  const { profile, save } = usePaymentProfile();
  const { logout } = useSocialContext();
  const email = profile?.email || '';

  const [code, setCode] = React.useState('');
  const [confirming, setConfirming] = React.useState(false);
  const [resending, setResending] = React.useState(false);
  const [cooldown, setCooldown] = React.useState(0);
  const [editing, setEditing] = React.useState(false);
  const [newEmail, setNewEmail] = React.useState('');
  const [savingEmail, setSavingEmail] = React.useState(false);

  React.useEffect(() => {
    if (cooldown <= 0) return;
    const t = setTimeout(() => setCooldown(c => c - 1), 1000);
    return () => clearTimeout(t);
  }, [cooldown]);

  const confirm = async () => {
    const clean = code.trim();
    if (clean.length !== 6) {
      message.error('El código tiene 6 dígitos.');
      return;
    }
    setConfirming(true);
    try {
      const r = await fetch(`${API_BASE}/drupi/email-verification/confirm`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify({ code: clean }),
      });
      if (!r.ok) {
        message.error(await detailOf(r, 'No se pudo verificar el código.'));
        return;
      }
      const d = await r.json();
      // El JWT nuevo lleva el email como claim — reemplaza al viejo antes del reload.
      if (d?.jwt) localStorage.setItem(JWT_KEY, d.jwt);
      message.success('¡Email verificado!');
      window.location.reload();
    } catch {
      message.error('No se pudo verificar el código. Revisá tu conexión.');
    } finally {
      setConfirming(false);
    }
  };

  const resend = async () => {
    setResending(true);
    try {
      const r = await fetch(`${API_BASE}/drupi/email-verification/request`, {
        method: 'POST',
        headers: authHeaders(),
      });
      if (!r.ok) {
        message.error(await detailOf(r, 'No se pudo enviar el código.'));
        return;
      }
      message.success(`Código enviado a ${email}`);
      setCooldown(RESEND_COOLDOWN_S);
    } catch {
      message.error('No se pudo enviar el código. Revisá tu conexión.');
    } finally {
      setResending(false);
    }
  };

  const saveNewEmail = async () => {
    const clean = newEmail.trim();
    if (!EMAIL_RE.test(clean)) {
      message.error('Ingresá un email válido (con @).');
      return;
    }
    setSavingEmail(true);
    try {
      // El PUT dispara solo el código nuevo a la casilla nueva (y desverifica).
      await save({ email: clean });
      message.success(`Código enviado a ${clean}`);
      setEditing(false);
      setNewEmail('');
      setCode('');
      setCooldown(RESEND_COOLDOWN_S);
    } catch (e) {
      // save() ya trae el motivo real (p.ej. 409 "ese email ya está en uso").
      message.error((e as Error).message || 'No se pudo cambiar el email');
    } finally {
      setSavingEmail(false);
    }
  };

  return (
    <div style={{
      minHeight: '100vh', background: '#f8f9fa', display: 'flex',
      alignItems: 'center', justifyContent: 'center', padding: 24,
    }}>
      <Card style={{ maxWidth: 440, width: '100%', borderRadius: 16, textAlign: 'center' }}
            styles={{ body: { padding: '36px 28px' } }}>
        <div style={{
          width: 64, height: 64, borderRadius: '50%', margin: '0 auto 18px',
          background: tokens.colors.brand.gradient, display: 'flex',
          alignItems: 'center', justifyContent: 'center',
        }}>
          <MailOutlined style={{ fontSize: 28, color: '#fff' }} />
        </div>

        <Title level={3} style={{ marginBottom: 8 }}>Verificá tu email</Title>
        <Text type='secondary' style={{ display: 'block', marginBottom: 20 }}>
          Para usar la app necesitamos confirmar tu casilla. Te mandamos un código de
          6 dígitos a <strong>{email}</strong>. Si no lo recibiste (mirá también en
          spam), tocá «Reenviar código».
        </Text>

        <Input
          size='large'
          placeholder='000000'
          maxLength={6}
          inputMode='numeric'
          value={code}
          onChange={ev => setCode(ev.target.value.replace(/\D/g, ''))}
          onPressEnter={confirm}
          style={{
            textAlign: 'center', fontSize: 26, letterSpacing: 10,
            fontWeight: 700, maxWidth: 260, margin: '0 auto',
          }}
        />

        <Button type='primary' size='large' block loading={confirming} onClick={confirm}
                style={{ marginTop: 18, maxWidth: 260 }}>
          Verificar
        </Button>

        <div style={{ marginTop: 14 }}>
          <Button type='link' loading={resending} disabled={cooldown > 0} onClick={resend}>
            {cooldown > 0 ? `Reenviar código (${cooldown}s)` : 'Reenviar código'}
          </Button>
        </div>

        {!editing ? (
          <div style={{ marginTop: 4 }}>
            <Button type='link' onClick={() => { setEditing(true); setNewEmail(email); }}>
              ¿Te equivocaste de email? Cambialo
            </Button>
          </div>
        ) : (
          <div style={{ marginTop: 12, display: 'flex', flexDirection: 'column', gap: 10 }}>
            <Input
              prefix={<MailOutlined style={{ color: '#9ca3af' }} />}
              placeholder='tu@email.com'
              value={newEmail}
              onChange={ev => setNewEmail(ev.target.value)}
              onPressEnter={saveNewEmail}
            />
            <div style={{ display: 'flex', gap: 8, justifyContent: 'center' }}>
              <Button onClick={() => setEditing(false)}>Cancelar</Button>
              <Button type='primary' loading={savingEmail} onClick={saveNewEmail}>
                Guardar y enviar código
              </Button>
            </div>
          </div>
        )}

        <div style={{ marginTop: 22, borderTop: '1px solid rgba(0,0,0,0.06)', paddingTop: 14 }}>
          <Button type='text' size='small' style={{ color: '#9ca3af' }} onClick={logout}>
            Salir
          </Button>
        </div>
      </Card>
    </div>
  );
};
